// Route: /dev/showcase
// Purpose: Renders shared components for Camera visual validation during cert runs.
// No auth guard — same access level as /dev-hub.
// Uses Vite's import.meta.glob to discover components without breaking build when they don't exist.
import React, { Suspense, useState } from 'react';

// Vite glob: discovers all .tsx in this directory at build time.
// Only components that exist get included. Missing ones = no build error.
const modules = import.meta.glob('./*.tsx') as Record<string, () => Promise<{ default: React.ComponentType<any> }>>;

interface ShowcaseEntry {
  name: string;
  Component: React.LazyExoticComponent<React.ComponentType<any>>;
  props: Record<string, any>;
}

// Components we want to showcase with their demo props.
// Only entries whose files actually exist (in the glob) get rendered.
const SHOWCASE_CONFIG: Record<string, Record<string, any>> = {
  StatusBadge: { status: 'active', size: 'md' },
  LoadingSpinner: { size: 40, color: '#E11D48', label: 'Loading...' },
  EmptyState: { title: 'No Data Yet', message: 'Upload your first lab report to get started.', icon: '\u{1F4CB}' },
  InfoCard: { title: 'Biomarkers', value: 42, subtitle: 'Total extracted', highlight: true },
  ProgressBar: { value: 72, color: '#E11D48', height: 8, showLabel: true },
  Tooltip: { text: 'Helpful tooltip text', children: null, position: 'top' },
  Avatar: { name: 'Stan Veit', size: 40 },
  Toggle: { checked: true, onChange: () => {}, label: 'Enable notifications' },
  Tag: { label: 'Active', variant: 'success' },
  Divider: { label: 'Section Break', spacing: 24 },
};

function buildEntries(): ShowcaseEntry[] {
  const entries: ShowcaseEntry[] = [];
  for (const [name, props] of Object.entries(SHOWCASE_CONFIG)) {
    const key = `./${name}.tsx`;
    if (modules[key]) {
      entries.push({
        name,
        Component: React.lazy(modules[key] as () => Promise<{ default: React.ComponentType<any> }>),
        props,
      });
    }
  }
  return entries;
}

export function ComponentShowcase() {
  const [entries] = useState(buildEntries);

  return (
    <div style={{ padding: 32, maxWidth: 800, margin: '0 auto', fontFamily: 'DM Sans, sans-serif', background: '#FAFAFA', minHeight: '100vh' }}>
      <h1 style={{ fontFamily: 'Instrument Serif, serif', fontSize: 28, marginBottom: 8, color: '#18181B' }}>
        Component Showcase
      </h1>
      <p style={{ fontSize: 14, color: '#71717A', marginBottom: 32 }}>
        Visual validation surface for Camera certification. {entries.length} component{entries.length !== 1 ? 's' : ''} found.
      </p>

      {entries.map((entry) => (
        <div
          key={entry.name}
          style={{
            marginBottom: 24,
            padding: 16,
            border: '1px solid #e5e7eb',
            borderRadius: 4,
            background: '#FFFFFF',
          }}
        >
          <div style={{ fontSize: 13, color: '#71717A', marginBottom: 8, fontFamily: 'JetBrains Mono, monospace' }}>
            {entry.name}
          </div>
          <Suspense fallback={<div style={{ color: '#71717A', fontSize: 13 }}>Loading...</div>}>
            <entry.Component {...entry.props} />
          </Suspense>
        </div>
      ))}

      {entries.length === 0 && (
        <div style={{ textAlign: 'center', padding: 48, color: '#71717A' }}>
          No components found. Run cert batch to create components.
        </div>
      )}
    </div>
  );
}
